import { useEffect, useRef, useState } from "react";
import { api, INSTAGRAM_URL } from "@/lib/config";
import { toast } from "sonner";
import { Instagram, Trash2, Loader2, Plus, ArrowUp, ArrowDown } from "lucide-react";

export default function StoryManager({ secret }) {
  const headers = { "X-Admin-Secret": secret };
  const [stories, setStories] = useState([]);
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [saving, setSaving] = useState(false);
  const coverRef = useRef(null);

  const load = async () => {
    const r = await api.get("/admin/stories", { headers });
    setStories(r.data.stories || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("instagram_url", url);
      fd.append("caption", caption);
      const f = coverRef.current?.files?.[0];
      if (f) fd.append("cover", f);
      await api.post("/admin/stories", fd, { headers });
      toast.success("Story added to the home page");
      setUrl("");
      setCaption("");
      if (coverRef.current) coverRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add that story");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this story from the home page?")) return;
    await api.delete(`/admin/stories/${id}`, { headers });
    toast.success("Story removed");
    load();
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= stories.length) return;
    const a = stories[index], b = stories[target];
    await Promise.all([
      api.patch(`/admin/stories/${a.id}`, { position: b.position }, { headers }),
      api.patch(`/admin/stories/${b.id}`, { position: a.position }, { headers }),
    ]);
    load();
  };

  return (
    <div data-testid="admin-story-manager" className="mt-8 grid lg:grid-cols-12 gap-8">
      <form onSubmit={add} className="lg:col-span-4 bg-[#FFFFFF] border border-[#E2D5C3] rounded-3xl shadow-[0_4px_20px_-2px_rgba(35,23,14,0.06)] p-6 h-fit">
        <p className="overline">Add a guide story</p>
        <p className="text-xs text-[#23170E]/55 mt-2 leading-relaxed">
          Post the clip or photo on Instagram first, then paste its link here. It appears as a tappable circle
          on the home page. <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="text-[#A6521B]">Open Instagram</a>
        </p>

        <label className="overline block mt-6">Instagram post / reel link *</label>
        <input
          data-testid="story-url-input"
          required
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.instagram.com/reel/ABC123/"
          className="mt-2 w-full bg-[#FAF6F0] border border-[#BCA993] rounded-full px-5 py-3 text-sm focus:outline-none focus:border-[#A6521B]"
        />

        <label className="overline block mt-5">Caption shown under the circle</label>
        <input
          data-testid="story-caption-input"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="e.g. Lion cubs, Ndutu"
          className="mt-2 w-full bg-[#FAF6F0] border border-[#BCA993] rounded-full px-5 py-3 text-sm focus:outline-none focus:border-[#A6521B]"
        />

        <label className="overline block mt-5">Thumbnail image (optional)</label>
        <input
          ref={coverRef}
          data-testid="story-cover-input"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="mt-2 w-full text-xs text-[#23170E]/70"
        />
        <p className="text-xs text-[#23170E]/45 mt-2">Instagram doesn't share thumbnails, so upload one — otherwise the circle shows the Instagram mark.</p>

        <button data-testid="story-add-btn" type="submit" disabled={saving} className="mt-6 w-full btn-pill btn-pill-primary justify-center disabled:opacity-60">
          {saving ? <><Loader2 size={14} className="animate-spin" /> Adding…</> : <><Plus size={14} /> Add story</>}
        </button>
      </form>

      <div className="lg:col-span-8">
        <div className="flex items-baseline justify-between">
          <h3 className="font-marcellus text-2xl">Home page stories</h3>
          <span className="text-xs text-[#23170E]/55">{stories.length} live</span>
        </div>

        {stories.length === 0 ? (
          <div data-testid="story-empty-state" className="mt-4 border border-dashed border-[#23170E]/20 rounded-3xl p-12 text-center text-[#23170E]/55">
            <Instagram className="mx-auto mb-3" size={28} />
            No stories yet. The strip stays hidden on the home page until you add one.
          </div>
        ) : (
          <div className="mt-4 grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {stories.map((s, i) => (
              <div key={s.id} data-testid={`story-item-${s.id}`} className="bg-[#FFFFFF] border border-[#E2D5C3] rounded-2xl shadow-[0_4px_20px_-2px_rgba(35,23,14,0.06)] overflow-hidden">
                <div className="aspect-square bg-[#23170E] flex items-center justify-center">
                  {s.has_cover ? (
                    <img src={`/api/stories/${s.id}/cover`} alt={s.caption} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <Instagram size={30} className="text-[#C88B35]" />
                  )}
                </div>
                <div className="p-3 space-y-2">
                  <input
                    data-testid={`story-caption-${s.id}`}
                    defaultValue={s.caption}
                    placeholder="Add a caption"
                    onBlur={(e) => e.target.value !== s.caption && api.patch(`/admin/stories/${s.id}`, { caption: e.target.value }, { headers }).then(load)}
                    className="w-full bg-transparent border-b border-[#BCA993] text-sm py-1 focus:outline-none focus:border-[#A6521B]"
                  />
                  <a href={s.instagram_url} target="_blank" rel="noreferrer" className="block text-xs text-[#A6521B] truncate">{s.instagram_url}</a>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex gap-2">
                      <button data-testid={`story-up-${s.id}`} onClick={() => move(i, -1)} disabled={i === 0} className="text-[#23170E]/60 disabled:opacity-30"><ArrowUp size={14} /></button>
                      <button data-testid={`story-down-${s.id}`} onClick={() => move(i, 1)} disabled={i === stories.length - 1} className="text-[#23170E]/60 disabled:opacity-30"><ArrowDown size={14} /></button>
                    </div>
                    <button data-testid={`story-delete-${s.id}`} onClick={() => remove(s.id)} className="text-[#A6352B] flex items-center gap-1"><Trash2 size={12} /> Remove</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
